import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'rsuite';
import { useAuth } from '@app/api/auth/useAuth';
import { getLang, withAuthLang } from '@app/i18n';
import { icons } from '@shared/static/icons';
import AuthLayout from '@shared/ui/auth/AuthLayout';
import { ThemeButton } from '@shared/ui/buttons';
import { FormGroup } from '@shared/ui/forms/display';
import { PasswordInput } from '@shared/ui/forms/inputs';
import Note from '@shared/ui/messages/Note';
import { isEmptyObject } from '@utils/helpers';
import { passwordMatchRule, validateServerErrors } from '@utils/validation';

export default function ActivateUserPage() {
  const [isValidateLoading, setIsValidateLoading] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { activateUser, validateActivationToken } = useAuth();
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      password: '',
      password_confirmation: '',
      terms_accept: false,
    },
  });

  useEffect(() => {
    async function validateToken() {
      try {
        const res = await validateActivationToken({ token });
        setIsTokenValid(res);
      } catch (error) {
        setIsTokenValid(false);
      } finally {
        setIsValidateLoading(false);
      }
    }
    validateToken();
  }, []);

  async function onSubmit(data) {
    try {
      await activateUser(data, token);
    } catch (error) {
      validateServerErrors(setError, error);
    }
  }

  return (
    <AuthLayout className="activate">
      {!isValidateLoading &&
        (isTokenValid ? (
          <form className="auth__block" onSubmit={handleSubmit(onSubmit)}>
            <div className="block-pd-x block-pd-y">
              <div className="customDialog__heading-icon">
                {icons['check-success']}
                <p className="title-h2m">{t('account_activation')}</p>
              </div>
              <p className="auth__subtitle">
                {t('account_activation_subtitle_1')}
              </p>
              <Note
                html
                type="warning"
                marginBottom
                title={t('password_requirements')}
                content={t('password_rule')}
              />
              <div className="form">
                <PasswordInput
                  error={errors.password}
                  control={control}
                  name="password"
                  label={t('password')}
                  placeholder={t('enter_password')}
                  onValueChange={(newPassword) => {
                    if (
                      newPassword.trim() &&
                      getValues('password_confirmation').trim()
                    ) {
                      trigger('password_confirmation');
                    }
                  }}
                />
                <PasswordInput
                  error={errors.password_confirmation}
                  control={control}
                  name="password_confirmation"
                  label={t('confirm_password')}
                  validation={(value) =>
                    passwordMatchRule(value, getValues('password'))
                  }
                  placeholder={t('enter_password')}
                />
                <FormGroup error={errors.terms_accept}>
                  <Controller
                    control={control}
                    name="terms_accept"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Checkbox
                        onChange={(_, checked) => field.onChange(checked)}
                        checked={field.value}
                      >
                        {t('i_have_read_and_agree_to_the')}{' '}
                        <a
                          href={`https://www.moneta.ad/${getLang()}/terms`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t('terms_of_service')}
                        </a>{' '}
                        {t('and')}{' '}
                        <a
                          href={`https://www.moneta.ad/${getLang()}/privacy`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t('privacy_policy')}
                        </a>
                      </Checkbox>
                    )}
                  />
                </FormGroup>
              </div>
            </div>
            <div className="dialog-actions btns-toolbar">
              <ThemeButton
                type="submit"
                startIcon="check"
                loading={isSubmitting}
                disabled={!isEmptyObject(errors)}
                block={false}
                minWidth
              >
                {t('confirm')}
              </ThemeButton>
            </div>
          </form>
        ) : (
          <div className="auth__block">
            <div className="block-pd-x block-pd-y">
              <div className="customDialog__heading-icon">
                {icons.error}
                <p className="title-h2m">{t('account_activation')}</p>
              </div>
              <p>{t('account_activation_expired')}</p>
            </div>
            <div className="dialog-actions">
              <ThemeButton
                startIcon="chevron-left"
                onClick={() => navigate(withAuthLang('/sign-up'))}
              >
                {t('return_to_registration_page')}
              </ThemeButton>
            </div>
          </div>
        ))}
    </AuthLayout>
  );
}
